import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { updateTitleParent } from '../../../api/endpoints/corporate';
import type { Title } from '../../../api/types/corporate';
import { ApiErrorAlert } from '../../../components/ApiErrorAlert';

/** Quick "move" action wired to the dedicated CorporateTitle/UpdateParent route — see the
 *  Department module's analogous dialog for the same rationale (kept distinct from the full
 *  edit form per the migration guide's page inventory). */
export function TitleReparentDialog({
  open,
  title,
  options,
  excludeIds,
  onClose,
}: {
  open: boolean;
  title: Title | null;
  options: { id: number; label: string }[];
  excludeIds: Set<number>;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [newParentId, setNewParentId] = useState<number | ''>('');

  useEffect(() => {
    if (open) setNewParentId(title?.parentTitleId ?? '');
  }, [open, title]);

  const mutation = useMutation({
    mutationFn: () =>
      updateTitleParent({
        titleId: title!.id,
        parentTitleId: newParentId === '' ? null : newParentId,
      }),
    onSuccess: () => {
      enqueueSnackbar('Üst unvan güncellendi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['corporate', 'titles'] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const selectableOptions = options.filter((o) => !excludeIds.has(o.id));

  return (
    <Dialog open={open} onClose={() => !mutation.isPending && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Üst Unvanı Değiştir</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            "{title?.name}" için yeni üst unvanı seçin.
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            label="Üst Unvan"
            value={newParentId}
            onChange={(e) => setNewParentId(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <MenuItem value="">Kök (üst unvan yok)</MenuItem>
            {selectableOptions.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <ApiErrorAlert error={mutation.error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Vazgeç
        </Button>
        <Button onClick={() => mutation.mutate()} variant="contained" disabled={mutation.isPending}>
          {mutation.isPending ? 'Kaydediliyor…' : 'Taşı'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
